// src/components/QueryComponent/chartUtils.js

/**
 * Calculates a trend line using simple linear regression
 * @param {Array} data - Array of data points
 * @param {string} xAxis - The name of the x-axis field
 * @param {string} yAxis - The name of the y-axis field
 * @param {string} chartType - Type of chart ('bar', 'line', etc.)
 * @param {Array} allYAxes - All Y axes (to check for forecast pairs)
 * @returns {Array} - Array of points for the trend line
 */
export const calculateTrendLine = (data, xAxis, yAxis, chartType = 'line', allYAxes = []) => {
  if (!data || !Array.isArray(data) || data.length < 2 || !xAxis || !yAxis) {
    return null;
  }

  // Check if this is an actual column with a corresponding forecast
  // First check if yAxis itself is already a forecast column
  const isForecastColumn = yAxis.includes(' - forecast');
  
  let forecastColumn;
  let hasForecastPair = false;
  
  if (isForecastColumn) {
    // If yAxis is already a forecast column, don't look for a pair
    hasForecastPair = false;
  } else {
    // Check for any forecast column that starts with the same base name
    // This handles both "column - forecast" and "column - forecast (Method)"
    const baseColumnName = yAxis;
    const forecastColumns = allYAxes.filter(col => 
      col.startsWith(`${baseColumnName} - forecast`)
    );
    
    if (forecastColumns.length > 0) {
      // Use the primary forecast (without method name) if available,
      // otherwise use the first forecast column found
      forecastColumn = forecastColumns.find(col => col === `${baseColumnName} - forecast`) 
                      || forecastColumns[0];
      hasForecastPair = true;
    }
  }
  
  // Extract numerical values
  const points = data.map((item, index) => {
    // For categorical x-axes, use the index as the x value
    let x;
    if (typeof item[xAxis] === 'number') {
      x = item[xAxis];
    } else if (typeof item[xAxis] === 'object' && item[xAxis]?.sortValue !== undefined) {
      // Handle processed data with sortValue property
      x = typeof item[xAxis].sortValue === 'number' ? item[xAxis].sortValue : index;
    } else {
      // Fallback to using the index for non-numeric x values
      x = index;
    }

    // Get y value - combine actual and forecast if available
    let y = null;

    // First try to get the actual value
    if (typeof item[yAxis] === 'number' && item[yAxis] !== null) {
      y = item[yAxis];
    } else if (typeof item[yAxis] === 'object' && item[yAxis]?.sortValue !== undefined && item[yAxis].sortValue !== null) {
      y = typeof item[yAxis].sortValue === 'number' ? item[yAxis].sortValue : null;
    } else if (item[yAxis] !== null && item[yAxis] !== undefined) {
      // Try to convert to number if not null/undefined
      const numValue = Number(item[yAxis]);
      y = !isNaN(numValue) ? numValue : null;
    }

    // If we have a forecast pair, try to use it when actual is null/undefined
    // This ensures we use forecast values for future periods
    if (hasForecastPair) {
      // Check if we should use the forecast value
      const shouldUseForecast = (y === null || y === undefined);
      
      if (shouldUseForecast) {
        // Try to get forecast value
        let forecastValue = null;
        
        if (typeof item[forecastColumn] === 'number' && item[forecastColumn] !== null) {
          forecastValue = item[forecastColumn];
        } else if (typeof item[forecastColumn] === 'object' && item[forecastColumn]?.sortValue !== undefined) {
          forecastValue = typeof item[forecastColumn].sortValue === 'number' ? item[forecastColumn].sortValue : null;
        } else if (item[forecastColumn] !== null && item[forecastColumn] !== undefined) {
          // Try to convert to number if not null/undefined
          const numValue = Number(item[forecastColumn]);
          forecastValue = !isNaN(numValue) ? numValue : null;
        }
        
        // Use forecast value if we found one
        if (forecastValue !== null) {
          y = forecastValue;
          console.log(`Using forecast value ${forecastValue} for index ${index}`);
        }
      }
    }

    return { x, y };
  }).filter(point => point.y !== null);

  // Not enough valid points
  if (points.length < 2) {
    return null;
  }

  // Debug logging
  console.log(`Trend line calculation for ${yAxis}:`, {
    totalDataPoints: data.length,
    validPoints: points.length,
    hasForecastPair,
    points: points.slice(0, 5), // Show first 5 points
    lastPoints: points.slice(-5), // Show last 5 points
  });

  // Calculate the means
  const n = points.length;
  const sumX = points.reduce((sum, point) => sum + point.x, 0);
  const sumY = points.reduce((sum, point) => sum + point.y, 0);
  const meanX = sumX / n;
  const meanY = sumY / n;

  // Calculate the coefficients
  let numerator = 0;
  let denominator = 0;

  for (let i = 0; i < n; i++) {
    const diffX = points[i].x - meanX;
    numerator += diffX * (points[i].y - meanY);
    denominator += diffX * diffX;
  }

  // Avoid division by zero
  if (denominator === 0) {
    return null;
  }

  const slope = numerator / denominator;
  const intercept = meanY - slope * meanX;

  // Check if all original data points are non-negative
  const hasNegativeValues = points.some(point => point.y < 0);
  
  // Generate trend line points (using original x-axis values for alignment)
  return data.map((item, index) => {
    const x = item[xAxis];
    const xVal = points.find(p => p.x === index)?.x || index;
    let yVal = slope * xVal + intercept;
    
    // If original data has no negative values, ensure trend line doesn't go negative
    // This applies to all chart types, not just bar charts
    if (!hasNegativeValues && yVal < 0) {
      yVal = 0;
    }
    
    return {
      [xAxis]: x,
      [`${yAxis}Trend`]: yVal,
    };
  });
};

/**
 * Generates styles for the trend line
 * @param {string} colorBase - Base color of the data series
 * @param {string} chartType - Type of chart
 * @param {boolean} isCombinedTrend - Whether this trend combines actual and forecast
 * @returns {Object} - Style object for the trend line
 */
export const getTrendLineStyle = (colorBase, chartType, isCombinedTrend = false) => {
  return {
    stroke: isCombinedTrend ? '#9B59B6' : (colorBase || '#8884d8'), // Purple for combined trends
    strokeWidth: chartType === 'bar' ? 3 : 2, // Thicker for bar charts
    strokeDasharray: isCombinedTrend ? '10 5' : '5 5', // Different dash pattern for combined
    opacity: chartType === 'bar' ? 0.9 : 0.7, // Higher opacity for bar charts
    activeDot: false,
    // Add this for bar charts to make line appear on top
    zIndex: chartType === 'bar' ? 1000 : 5,
    // For bar charts, make the line stand out more
    strokeLinecap: 'round',
  };
};